import { ExternalLink } from '@/components/common/ExternalLink'
import type { Run } from '@/lib/types'
import { formatDuration } from '@/lib/utils'

export interface RunMetadataProps {
  /** Run data */
  run: Run
  /** Additional CSS classes */
  className?: string
}

/**
 * Key-value pairs display for run metadata.
 * 
 * @example
 * ```tsx
 * <RunMetadata run={run} />
 * ```
 */
export function RunMetadata({ run, className }: RunMetadataProps) {
  const repoName = run.repo_url?.split('/').slice(-2).join('/') ?? '—'
  
  // Calculate duration
  let duration = '—'
  if (run.timestamps?.started) {
    const start = new Date(run.timestamps.started)
    const end = run.timestamps.finished ? new Date(run.timestamps.finished) : new Date()
    const diffSeconds = Math.floor((end.getTime() - start.getTime()) / 1000)
    duration = formatDuration(diffSeconds)
    if (!run.timestamps.finished) {
      duration += ' (running)'
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—'
    const date = new Date(dateStr)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
  }

  return (
    <div className={className}>
      <dl className="space-y-3 text-sm">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Repository:</dt>
          <dd>
            {run.repo_url ? (
              <ExternalLink href={run.repo_url}>{repoName}</ExternalLink>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground flex-shrink-0">Branch:</dt>
          <dd className="font-mono text-xs break-all text-right">{run.branch ?? run.base_branch ?? '—'}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Runner:</dt>
          <dd>{run.runner_profile || 'default'}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Timeout:</dt>
          <dd>{run.timeout_seconds ? `${run.timeout_seconds}s` : '—'}</dd>
        </div>
        {run.work_item_id && (
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Work Item:</dt>
            <dd>
              <ExternalLink href={`#`}>
                {run.work_item_id}
              </ExternalLink>
            </dd>
          </div>
        )}
        <div className="border-t pt-3 mt-3">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Created:</dt>
            <dd>{formatDate(run.timestamps?.created)}</dd>
          </div>
        </div>
        {run.timestamps?.started && (
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Started:</dt>
            <dd>{formatDate(run.timestamps.started)}</dd>
          </div>
        )}
        {run.timestamps?.finished && (
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Finished:</dt>
            <dd>{formatDate(run.timestamps.finished)}</dd>
          </div>
        )}
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Duration:</dt>
          <dd>{duration}</dd>
        </div>
      </dl>
    </div>
  )
}
